import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-encouragement",
  title: "8 Bible verses about encouragement, and who they were for",
  h1: "Bible verses about encouragement",
  description:
    "Eight passages from the World English Bible with a note on each — most of them addressed to people in genuine trouble, which is what makes them usable in it.",
  targets: [
    "bible verses about encouragement",
    "encouraging scripture for hard times",
    "verses to encourage someone",
  ],
  published: "2026-09-03",
  standfirst:
    "The encouraging verses that hold are the ones spoken into something bad. The ones that fail are the ones assumed to be spoken into nothing.",
  takeaways: [
    "Almost every verse here was said to somebody in real trouble — an exile, a new leader, a persecuted church — which is precisely why they survive being used in it.",
    "Half of the New Testament's language about encouragement is plural and mutual: exhort one another, build each other up. It is not primarily a private activity.",
    "Isaiah 41:13 is physical rather than motivational — the image is a hand being held, which is what you offer somebody who cannot walk on their own.",
    "An encouraging verse sent without a message of your own is usually heard as a way of not having the conversation.",
  ],
  intro: [
    {
      type: "p",
      text: "Encouraging is fourth on the most-searched topic list of the largest topical Bible site, and it is usually searched on behalf of somebody else — a friend in a bad month, a family member facing something. The eight below are the ones that keep being reached for, with a note on each saying who it was originally said to.",
    },
    {
      type: "p",
      text: "That matters more here than in most collections. A verse of encouragement is only as good as its address: told to somebody in a hospital corridor, a line originally spoken to a comfortable person sounds like a slogan, and a line spoken to an exile sounds like company.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "For I, Yahweh your God, will hold your right hand, saying to you, 'Don't be afraid. I will help you.'",
        ref: "Isaiah 41:13",
      },
      note: "The image is physical and small: a hand being held. Not a rescue, not a solution, not an explanation. It is addressed to people in exile who had every reason to think they had been abandoned, and what is offered is the thing you offer somebody who cannot walk unaided — which is a considerably more honest gesture than most encouragement.",
    },
    {
      verse: {
        text: "Therefore exhort one another, and build each other up, even as you also do.",
        ref: "1 Thessalonians 5:11",
      },
      note: "Plural and mutual, and the last clause is worth noticing: even as you also do. Paul is not introducing a new duty but naming something already happening and telling them to keep at it. Encouragement in the New Testament is overwhelmingly a thing done between people rather than a private practice, and this verse sits in a run of short instructions about ordinary community life.",
    },
    {
      verse: {
        text: "Let's consider how to provoke one another to love and good works, not forsaking our own assembling together, as the custom of some is, but exhorting one another, and so much the more as you see the Day approaching.",
        ref: "Hebrews 10:24–25",
      },
      note: "Provoke is a strong and slightly abrasive word — the Greek is closer to irritate or stir up than to gently inspire. Encouragement here has friction in it. The context is people quietly stopping coming, which the writer names without contempt, and the answer offered is more contact rather than better arguments.",
    },
    {
      verse: {
        text: "Be strong, and let your heart take courage, all you who hope in Yahweh.",
        ref: "Psalm 31:24",
      },
      note: "The last line of a psalm that has spent twenty-three verses on being surrounded, forgotten like a dead man, and slandered. That is the arc worth knowing: the encouragement is the closing sentence of a complaint rather than the whole psalm's mood, and it is addressed to the reader by somebody who has just finished describing a bad situation.",
    },
    {
      verse: {
        text: "Therefore we don't faint, but though our outward person is decaying, yet our inward person is renewed day by day.",
        ref: "2 Corinthians 4:16",
      },
      note: "Written by a man listing beatings, imprisonments and a shipwreck, and it concedes the decay rather than denying it. That concession is what makes the second half usable: nothing here claims the body is fine or that the circumstances have improved. The renewal is described as daily, which is the same unit almost every durable promise in Scripture uses.",
    },
    {
      verse: {
        text: "Be strong and courageous. Don't be afraid or scared of them, for Yahweh your God himself is who goes with you. He will not fail you nor forsake you.",
        ref: "Deuteronomy 31:6",
      },
      note: "Said to a whole generation about to cross into territory they had refused to enter forty years earlier. The encouragement is grounded in accompaniment rather than in their own capability, and the two commands attached concede that fear and dread were realistic. Hebrews 13:5 later applies this sentence to Christians generally.",
    },
    {
      verse: {
        text: "Let's not be weary in doing good, for we will reap in due season if we don't give up.",
        ref: "Galatians 6:9",
      },
      note: "For the specific exhaustion of doing the right thing with nothing visible coming back. Note that the person addressed is still working — this is not a verse about resting but about continuing — and that in due season is deliberately unspecified. The vagueness is the hard part rather than a translation problem.",
    },
    {
      verse: {
        text: "Haven't I commanded you? Be strong and courageous. Don't be afraid. Don't be dismayed, for Yahweh your God is with you wherever you go.",
        ref: "Joshua 1:9",
      },
      note: "Grammatically a command rather than a reassurance, said to a man taking over from Moses with a campaign in front of him. Wherever you go is the operative phrase and it includes the places where it goes badly. Used as a general lift it is thin; used as what it is — an assignment with company attached — it holds.",
    },
    {
      verse: {
        text: "He gives power to the weak. He increases the strength of him who has no might.",
        ref: "Isaiah 40:29",
      },
      note: "Two clauses describing the same transaction, and the second is the sharper one: strength increased for the person who has none. Not topped up, not rewarded — given to somebody at zero. The verse immediately after it has the fittest young men falling over, which removes any suggestion that this is a reward for effort already made.",
    },
    {
      verse: {
        text: "For whatever things were written before were written for our learning, that through perseverance and through encouragement of the Scriptures we might have hope.",
        ref: "Romans 15:4",
      },
      note: "Paul's account of why the older writings are worth reading at all, and the mechanism he names is worth attention: perseverance and encouragement together produce hope. Not the other way round. Hope is the output of having kept going while being encouraged, which is a slower and more realistic sequence than the one most people assume.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "sending-one-to-somebody",
      text: "Sending one to somebody",
    },
    {
      type: "p",
      text: "A verse on its own, with no message around it, is frequently received as a way of avoiding the conversation. Say something of your own first — even one sentence naming what you know is happening — and then the passage reads as company rather than as a substitute for it.",
    },
    {
      type: "p",
      text: "And where you can, do something as well as saying something. The New Testament's language about encouragement is relentlessly practical, and {carrying part of the weight} is the version of it that costs you an afternoon.",
      links: { "carrying part of the weight": "/prayers/prayer-for-a-friend" },
    },
    {
      type: "p",
      text: "One more thing these passages share, and it is the least comfortable of them: almost none of the encouragement in Scripture is delivered at a distance. Paul writes letters, but the letters are full of plans to visit, names of people already there, and requests that somebody come before winter. The New Testament's assumption is that the encouragement arrives with a person attached to it.",
    },
    {
      type: "note",
      text: "The most useful encouragement is usually the one sent in month three, when everybody else has gone back to normal and the situation has not.",
    },
  ],
  faq: [
    {
      q: "What is the best Bible verse for encouragement?",
      a: "For somebody who cannot function, Isaiah 41:13 — a hand being held rather than a solution. For somebody doing the right thing with nothing coming back, Galatians 6:9. The right verse depends on what has run out.",
    },
    {
      q: "What do you say when sending someone a Bible verse?",
      a: "Something of your own first, naming what you know is happening. A verse sent alone is often heard as a way of not having the conversation.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all ten are printed whole rather than referred to. Four of them — Isaiah 41:13, Psalm 31:24, Deuteronomy 31:6 and Joshua 1:9 — render the divine name as Yahweh where most translations have 'the LORD'.",
    },
  ],
  related: ["phone-addiction-bible-verses", "how-to-memorize-bible-verses"],
  relatedTopics: ["bible-verses-about-strength", "bible-verses-about-grief"],
};
